import { useMemo } from 'react'
import { jwtDecode } from 'jwt-decode'
import { parseScopes } from '../constants/scopes'
import useJwt from './useJwt'
import useScopePreview from './useScopePreview'
import type { JWTPayload } from '../types'

/**
 * User info derived from JWT
 */
export interface UserInfo {
  email: string
  name: string
  avatar_url: string | null
  /**
   * The scopes the UI should gate on: the previewed set when "View as" is
   * active, otherwise `realScopes`. Not `ScopeName[]` — the sync puts roles in
   * here too.
   */
  scopes: string[]
  /** The raw `backoffice_scopes` claim, ignoring any preview. */
  realScopes: string[]
  isPreviewing: boolean
}

/**
 * Hook to get user info from JWT token
 * Backoffice JWTs contain: email, name, avatar_url, sub
 */
const useUserInfo = (): UserInfo | null => {
  // Keyed on the token: App mounts on /callback before it exists, so a [] memo
  // would stay null for the whole session after a fresh login.
  const token = useJwt()
  const previewScopes = useScopePreview()

  const userInfo = useMemo(() => {
    try {
      if (!token) {
        return null
      }
      const decoded = jwtDecode<JWTPayload>(token)
      // Claim is normally a JSON string, but the type also allows an array;
      // parseScopes handles both and coerces anything else (including a NULL
      // DB value serialized as the string "null") to [].
      const realScopes = parseScopes(decoded.backoffice_scopes)
      return {
        email: decoded.email || decoded.sub || '',
        name: decoded.name || decoded.email?.split('@')[0] || 'User',
        avatar_url: decoded.avatar_url || null,
        scopes: previewScopes ?? realScopes,
        realScopes,
        isPreviewing: previewScopes !== null
      }
    } catch (error) {
      console.error('Error decoding JWT:', error)
      return null
    }
  }, [token, previewScopes])

  return userInfo
}

export default useUserInfo
